// Rental — store rent payments (Admin). List with filters + pagination, and
// add / edit / view / soft-delete. The store list is the kioskLocation master
// the Feedback module already uses (getFeedbackLocations).
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import ApparelMenu from "../SubComponents/apparelMenu";
import {
    createOrUpdateRentalAction,
    getFeedbackLocationsAction,
    getRentalListAction,
} from "../../Store/actions/apparelAction";
import AppConstant from "../../Globals/AppConstants";
import { Button } from "ui/button";
import { Dialog, DialogContent, DialogTitle } from "ui/dialog";
import { ConfirmDialog } from "../../shared/confirm-dialog";
import { locationLabel } from "../Feedback/feedbackConstants";
import RentalTable from "./RentalTable";
import RentalFormSheet, { RentalFormMode } from "./RentalFormSheet";
import {
    AMBATTUR_STORE_PATTERN,
    buildFinancialYears,
    currentFinancialYearStart,
    isPdfFile,
    rentalFileUrl,
} from "./rentalConstants";

const PAGE_SIZE = 10;

const rentalFormTemp = {
    rentalId: "",
    rentalCode: "",
    storeId: "",
    floor: "",
    rentMonth: "",
    financialYear: "",
    paymentDate: moment().format("YYYY-MM-DD"),
    amount: "",
    attachmentFileUuid: null as string | null,
    remarks: "",
};

const SUCCESS_RESULTS = ["Created Successfully", "Updated Successfully", "Deleted Successfully"];

const RentalScreen = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    // Arriving from the Audit Log: open the page that holds this rental, then
    // open its Edit sheet (the way a row's Edit action does).
    const auditRecordId: string | undefined = location.state?.auditRecordId;
    const [seekPending, setSeekPending] = useState(!!auditRecordId);
    const [pendingOpenId, setPendingOpenId] = useState<string | undefined>(auditRecordId);
    // Set once the request that asks for the record's page has gone out, so a
    // stale list left in redux from an earlier visit can't be mistaken for its answer.
    const seekSent = useRef(false);
    const {
        getRentalListData, getRentalListOnLoad,
        createOrUpdateRentalOnLoad, createOrUpdateRentalData,
        getFeedbackLocations,
    } = useSelector((state: any) => state.ApparelReducerState);

    const rentalList = getRentalListData?.getRentalList?.rentalDetails;
    const rentalPage = getRentalListData?.getRentalList?.page;

    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<RentalFormMode>("add");
    const [formData, setFormData] = useState<any>(rentalFormTemp);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [pending, setPending] = useState<"save" | "delete" | null>(null);
    const [previewFile, setPreviewFile] = useState<string | null>(null);
    // The list opens on the current financial year. Arriving from the Audit
    // Log it opens on all years instead, so the linked rental is found
    // wherever its payment date falls.
    // Only the current financial year is offered besides "All Years".
    const financialYears = useMemo(() => buildFinancialYears(1), []);
    const initialYear = auditRecordId ? undefined : financialYears[0];
    const [yearFilter, setYearFilter] = useState(initialYear?.label ?? "");
    const [searchTemp, setSearchTemp] = useState({
        storeId: "",
        floor: "",
        rentMonth: "",
        fromDate: initialYear?.from ?? "",
        toDate: initialYear?.to ?? "",
        offset: 0,
        page: 1,
    });

    const storeOptions = useMemo(
        () =>
            (getFeedbackLocations?.getFeedbackLocations?.items || [])
                .filter((s: any) => s?.isActive)
                .map((s: any) => ({ value: String(s?.id), label: locationLabel(s) })),
        [getFeedbackLocations]
    );
    const defaultStoreId = useMemo(
        () => storeOptions.find((s: any) => AMBATTUR_STORE_PATTERN.test(s.label))?.value ?? "",
        [storeOptions]
    );

    const getRentalListApi = () => {
        const unfiltered = !searchTemp.storeId && !searchTemp.floor && !searchTemp.rentMonth && !searchTemp.fromDate && !searchTemp.toDate;
        if (seekPending && searchTemp.page === 1 && unfiltered) seekSent.current = true;
        dispatch(getRentalListAction({
            storeId: searchTemp.storeId,
            floor: searchTemp.floor,
            rentMonth: searchTemp.rentMonth,
            fromDate: searchTemp.fromDate,
            toDate: searchTemp.toDate,
            seekRentalId: seekPending && searchTemp.page === 1 && unfiltered ? auditRecordId : undefined,
            paging: { limit: PAGE_SIZE, offset: searchTemp.offset },
        }));
    };

    // The seek answers with the page that holds the record — move the pager
    // there once, then stop seeking.
    useEffect(() => {
        if (!seekPending || !seekSent.current || getRentalListOnLoad || !rentalPage?.currentPage) return;
        setSeekPending(false);
        if (rentalPage.currentPage !== searchTemp.page) {
            setSearchTemp((s) => ({ ...s, page: rentalPage.currentPage, offset: (rentalPage.currentPage - 1) * PAGE_SIZE }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rentalPage, getRentalListOnLoad]);

    // Once the answer to the seek request is in, open the linked rental's Edit
    // sheet. A rental that is no longer in the list (deleted) simply opens nothing.
    useEffect(() => {
        if (!pendingOpenId || seekPending || !seekSent.current || getRentalListOnLoad) return;
        const match = (rentalList || []).find((r: any) => r?.rentalId === pendingOpenId);
        if (!match) return;
        setPendingOpenId(undefined);
        openRow(match, "edit");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingOpenId, seekPending, rentalList, getRentalListOnLoad]);

    useEffect(() => {
        dispatch(getFeedbackLocationsAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getRentalListApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTemp]);

    const onPageChange = (nextPage: number) => {
        setSearchTemp({ ...searchTemp, page: nextPage, offset: (nextPage - 1) * PAGE_SIZE });
    };

    const onYearFilterChange = (label: string) => {
        const fy = financialYears.find((y) => y.label === label);
        setYearFilter(label);
        setSearchTemp({
            ...searchTemp,
            fromDate: fy?.from ?? "",
            toDate: fy?.to ?? "",
            offset: 0,
            page: 1,
        });
    };

    const rowToForm = (row: any) => ({
        rentalId: row?.rentalId,
        rentalCode: row?.rentalCode ?? "",
        storeId: row?.storeId != null ? String(row.storeId) : "",
        floor: row?.floor ?? "",
        rentMonth: row?.rentMonth != null ? String(row.rentMonth) : "",
        financialYear: row?.financialYear != null ? String(row.financialYear) : "",
        paymentDate: row?.paymentDate ? String(row.paymentDate).slice(0, 10) : "",
        amount: row?.amount != null ? String(Math.round(Number(row.amount))) : "",
        attachmentFileUuid: row?.attachmentFileUuid ?? null,
        remarks: row?.remarks ?? "",
    });

    const openAdd = () => {
        setMode("add");
        setFormData({
            ...rentalFormTemp,
            paymentDate: moment().format("YYYY-MM-DD"),
            storeId: defaultStoreId,
            rentMonth: String(moment().month() + 1),
            financialYear: String(currentFinancialYearStart()),
        });
        setOpen(true);
    };

    const openRow = (row: any, nextMode: RentalFormMode) => {
        setMode(nextMode);
        setFormData(rowToForm(row));
        setOpen(true);
    };

    const formClose = () => {
        setOpen(false);
        setFormData(rentalFormTemp);
    };

    const submit = () => {
        setPending("save");
        dispatch(createOrUpdateRentalAction({
            rentalId: formData?.rentalId,
            storeId: formData?.storeId,
            floor: formData?.floor,
            rentMonth: formData?.rentMonth,
            financialYear: formData?.financialYear,
            paymentDate: formData?.paymentDate,
            amount: formData?.amount,
            attachmentFileUuid: formData?.attachmentFileUuid,
            remarks: formData?.remarks,
        }));
    };

    const confirmDelete = () => {
        setPending("delete");
        dispatch(createOrUpdateRentalAction({ rentalId: deleteTarget?.rentalId, isDeleted: 1 }));
    };

    // The mutation answers with a message string — anything other than a
    // *_SUCCESSFULLY text is a validation/authorization error.
    useEffect(() => {
        if (!pending || createOrUpdateRentalOnLoad) return;
        const result = createOrUpdateRentalData?.createOrUpdateRental;
        const wasDelete = pending === "delete";
        setPending(null);
        if (!SUCCESS_RESULTS.includes(result)) {
            toast.error(typeof result === "string" ? result : "Something went wrong");
            if (wasDelete) setDeleteTarget(null);
            return;
        }
        if (wasDelete) {
            toast.success("Rental deleted successfully");
            setDeleteTarget(null);
            // Deleting the only row on a later page steps back a page.
            if ((rentalList || []).length === 1 && searchTemp.page > 1) {
                onPageChange(searchTemp.page - 1);
                return;
            }
        } else {
            toast.success(result === "Created Successfully" ? "Rental added successfully" : "Rental updated successfully");
            formClose();
        }
        getRentalListApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createOrUpdateRentalOnLoad, pending]);

    const openAttachment = (row: any) => {
        const fileUuid = row?.attachmentFileUuid;
        if (!fileUuid) return;
        if (isPdfFile(fileUuid)) window.open(rentalFileUrl(fileUuid), "_blank", "noopener");
        else setPreviewFile(fileUuid);
    };

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="rental" />
            <RentalTable
                data={rentalList}
                loading={getRentalListOnLoad}
                onView={(row) => openRow(row, "view")}
                onEdit={(row) => openRow(row, "edit")}
                onDelete={setDeleteTarget}
                onAttachment={openAttachment}
                storeOptions={storeOptions}
                storeFilter={searchTemp.storeId}
                onStoreFilterChange={(storeId) => setSearchTemp({ ...searchTemp, storeId, offset: 0, page: 1 })}
                floorFilter={searchTemp.floor}
                onFloorFilterChange={(floor) => setSearchTemp({ ...searchTemp, floor, offset: 0, page: 1 })}
                monthFilter={searchTemp.rentMonth}
                onMonthFilterChange={(rentMonth) => setSearchTemp({ ...searchTemp, rentMonth, offset: 0, page: 1 })}
                financialYears={financialYears}
                yearFilter={yearFilter}
                onYearFilterChange={onYearFilterChange}
                title={AppConstant?.menus?.rental}
                headerActions={<Button onClick={openAdd}>{AppConstant?.add}</Button>}
                pagination={{
                    page: rentalPage?.currentPage || 1,
                    total: rentalPage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />
            <RentalFormSheet
                open={open}
                mode={mode}
                onClose={formClose}
                storeOptions={storeOptions}
                formData={formData}
                onChange={(patch: any) => setFormData({ ...formData, ...patch })}
                onSubmit={submit}
            />
            <ConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
                title="Delete rental?"
                description={`${deleteTarget?.rentalCode ?? "This rental"} will be removed from the list.`}
                confirmLabel="Delete"
                cancelLabel={AppConstant?.cancel}
                destructive
                loading={pending === "delete"}
                onConfirm={confirmDelete}
            />
            <Dialog open={!!previewFile} onOpenChange={(o) => { if (!o) setPreviewFile(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogTitle className="sr-only">Rental Attachment</DialogTitle>
                    {previewFile && (
                        <img
                            src={rentalFileUrl(previewFile)}
                            alt="Rental attachment"
                            className="max-h-[80vh] w-full rounded-lg object-contain"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RentalScreen;
